import { Language } from '@funnellab/shared/src/types/directus-enums.ts';
import {
  type PropsWithChildren,
  createContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useRouteLoaderData } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import fileService from '@src/api/services/FileService.ts';
import type { User } from '@src/api/services/AuthenticationService';
import { UserRole } from '@funnellab/shared/src/dtos/UserDTO';

export type UserContextType = {
  user: User;
  profilePictureUrl: string | null;
  setProfilePictureUrl: (url: string | null) => void;
  language: Language;
  setLanguage: (language: Language) => void;
  isAdminUser: boolean;
  isAgencyUser: boolean;
};

const UserContext = createContext<UserContextType | undefined>(undefined);

export default UserContext;

export const UserContextWrapper = ({ children }: PropsWithChildren) => {
  const { i18n } = useTranslation();
  const currentUser = useRouteLoaderData('root') as User | undefined | null;

  if (!currentUser) {
    throw new Error(
      'UserContext could not find the current user. Make sure to only use this component inside the root route that has a loader that fetches the current user.',
    );
  }

  const [profilePictureUrl, setProfilePictureUrl] = useState<string | null>(
    null,
  );

  const [language, setLanguage] = useState<Language>(currentUser.language);

  useEffect(() => {
    if (currentUser.avatar?.id) {
      const url = fileService.getFileDownloadUrl(currentUser.avatar.id);
      setProfilePictureUrl(url);
    } else {
      setProfilePictureUrl(null);
    }
  }, [currentUser.avatar?.id]);

  useEffect(() => {
    if (Object.values(Language).includes(currentUser.language)) {
      setLanguage(currentUser.language);
    } else {
      setLanguage(Language.DE);
    }
  }, [currentUser.language]);

  useEffect(() => {
    if (language && language !== (i18n.language as Language)) {
      void i18n.changeLanguage(language);
    }
  }, [i18n, language]);

  const isAdminUser = useMemo(
    () => currentUser.role === UserRole.ADMINISTRATOR,
    [currentUser.role],
  );
  const isAgencyUser = useMemo(
    () => currentUser.role === UserRole.AGENCY,
    [currentUser.role],
  );

  return (
    <UserContext.Provider
      value={{
        user: currentUser,
        profilePictureUrl,
        setProfilePictureUrl,
        language,
        setLanguage,
        isAdminUser,
        isAgencyUser,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
